import { z } from "zod";

/**
 * Common reusable Zod schemas for API operations across the server package.
 * Import these schemas instead of redeclaring them in individual schema files.
 *
 * Usage:
 * import { objectIdString, filterWhereClause, sortConfig, statusResponse } from "../../helpers/schemas/common.schema.js";
 */

// ============================================================================
// MongoDB ObjectId Schema
// ============================================================================

/**
 * MongoDB ObjectId as a 24-character hex string with validation.
 */
export const objectIdString = z
  .string()
  .trim()
  .regex(/^[a-fA-F0-9]{24}$/, "Invalid ObjectId format")
  .meta({
    description: "MongoDB ObjectId as 24-character hex string",
    example: "64b2c123df890abc1234abcd",
  })
  .brand<"ObjectId">();

/**
 * Tag an id ALREADY known to be a valid ObjectId as an {@link ObjectIdString} — values that came out
 * of MongoDB (`doc._id`, `insertedId`) or that the server generated, which have already passed the
 * trust boundary. Accepts an `ObjectId` structurally, so helpers needs no mongodb dependency.
 *
 * ⚠️ For anything CLIENT-supplied use `objectIdString.parse()` / `.safeParse()` instead — that
 * boundary is what the brand exists to protect.
 */
export function toObjectIdString(
  id: { toString(): string } | string,
): ObjectIdString;
export function toObjectIdString(
  id: { toString(): string } | string | null | undefined,
): ObjectIdString | undefined;
export function toObjectIdString(
  id: { toString(): string } | string | null | undefined,
): ObjectIdString | undefined {
  // Mirrors `x?._id?.toString()` semantics so nullish chains keep propagating undefined
  // instead of stringifying into the literal "undefined".
  return id == null ? undefined : (String(id) as ObjectIdString);
}

export const folderRoleId = z
  .union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
  ])
  .meta({
    description:
      "Numeric folder-role id (1-7) from folderRoles(); the service maps it to that role's permission-id set. Obtain valid ids from getFolderRolesList (the `id` field), NOT the role name.",
  });

export type FolderRoleId = z.infer<typeof folderRoleId>;

// ============================================================================
// Email Schema
// ============================================================================

/**
 * Email address, trimmed BEFORE validation. In Zod v4 a `.trim()` chained after `z.email()` runs
 * after validation, so surrounding whitespace fails the check — hence the `.pipe()`. Max 254 chars
 * (RFC 5321); callers add `.optional()` / `.nullable()`.
 */
export const emailString = z.string().trim().pipe(z.email().max(254)).meta({
  description:
    "Email address (trimmed, RFC-validated, max 254 chars per RFC 5321)",
  example: "user@example.com",
});

// ============================================================================
// AWS / Temp Upload Key Schema
// ============================================================================

/**
 * Key returned by `/fileSystemServer/uploadFileInTemp`. Shape is `<uuid-v4>.<ext>`
 * (see `packages/server/fileUpload/fileUpload.service.ts` – `file.__uuid__`).
 * Use this anywhere a request accepts an awsKey freshly produced by the temp
 * upload endpoint, so path-traversal / arbitrary strings are rejected before
 * they reach `transferFileFromTempToMainStorage`.
 */
export const tempUploadKey = z
  .string()
  .trim()
  .min(1)
  .max(256)
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[A-Za-z0-9]+$/i,
    "awsKey must be a uuid.<extension> produced by /fileSystemServer/uploadFileInTemp",
  )
  .meta({
    description:
      "Temp-upload key (uuid.<extension>) returned by /fileSystemServer/uploadFileInTemp",
    example: "f47ac10b-58cc-4372-a567-0e02b2c3d479.mp4",
  });

export const uuidStorageKey = z
  .string()
  .trim()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    "awsKey must be a bare uuid",
  )
  .meta({
    description:
      "Storage object key: a bare uuid, with no path separators or extension",
    example: "f47ac10b-58cc-4372-a567-0e02b2c3d479",
  });

// ============================================================================
// Grid/Query Filter Schemas
// ============================================================================

/**
 * The operator tokens `getFieldOperatorValue` (server/helpers/queryBuilder.ts) actually builds a
 * clause for — the single source of truth for the vocabulary, consumed by that switch's
 * `FilterOperator` union and by the `.meta({ description })` on every `operator` field below.
 *
 * Every `operator` field below is `z.enum(filterOperators)`, so this list is both PUBLISHED (as a
 * real JSON-Schema `enum`, plus {@link filterOperatorDescription} for the semantics) and ENFORCED.
 * It was previously neither: `operator` was a free `z.string()` that no schema described, so
 * `z.toJSONSchema` advertised it as a bare `{ "type": "string" }` — and MCP/API clients build their
 * filters from exactly that. An LLM caller guessed the conventional shorthand `eq`, and the query
 * builder then DROPPED the condition: `getFieldOperatorValue` reports the unhandled operator and
 * returns `{}`, which removes the clause from the `$and` and runs the query UNFILTERED. The caller
 * got 200 and every row rather than an error (staging: a grid route filtering `name` with `eq`
 * returned every row in the portal instead of the one named — Sentry "queryBuilder:
 * unhandled filter operator \"eq\" on field \"name\"", CUBE-31029). The enum stops the guess at the
 * boundary with a 400 that names the valid options, instead of answering with the wrong rows.
 *
 * Contrast `sortConfigFor`, whose `name` already both published and enforced its allow-list: the
 * sort half of the same payload was safe and the filter half was not.
 *
 * Keep in sync with that switch, in BOTH directions. A token here with no `case` is now WORSE than
 * before — it would pass validation and then silently no-op, which is the exact failure the enum
 * exists to prevent. A `case` missing from here turns a working filter into a 400.
 */
export const filterOperators = [
  "equal",
  "notequal",
  "in",
  "contains",
  "like",
  "startswith",
  "endswith",
  "doesnotcontain",
  "doesnotstartwith",
  "doesnotendwith",
  "greaterthan",
  "greaterthanorequal",
  "lessthan",
  "lessthanorequal",
  "isempty",
  "isnotempty",
  "doesexist",
  "doesnotExist",
  "globalsearch",
] as const;

/**
 * Model-facing SEMANTICS for {@link filterOperators}, shared by every `operator` field. The enum
 * itself already publishes the legal values, so this adds only what an `enum` list cannot say: which
 * token to reach for, that `equal` is case-insensitive, that `in` wants an array, and which tokens
 * ignore `value` entirely. It deliberately does NOT restate the full list — `z.toJSONSchema` emits
 * that as `enum` alongside this text, and duplicating it would double the size of every advertised
 * grid tool.
 */
export const filterOperatorDescription =
  'Comparison operator; see `enum` for the exact accepted tokens (note the capital E in "doesnotExist"). ' +
  'Shorthand such as "eq", "ne", "=" or "equals" is rejected with a 400 listing the valid options. ' +
  'Omitting `operator` entirely defaults to "equal". ' +
  'Use "equal" for an exact (case-insensitive) match, "contains" for a substring, "in" with an array ' +
  'of values for a set match; "isempty"/"isnotempty"/"doesexist"/"doesnotExist" test presence and ' +
  "ignore `value`.";

/**
 * Filter where clause for grid queries.
 * Accepts both record format (object) and array format (Syncfusion DataManager).
 */
export const filterWhereClause = z
  // record (object) OR array (Syncfusion DataManager) — same inputs the previous
  // z.custom accepted, but JSON-Schema-representable so it can be advertised to API/MCP clients.
  .union([z.record(z.string(), z.unknown()), z.array(z.unknown())])
  .optional()
  .meta({
    description:
      "Filter conditions for grid queries (supports both record and array formats). The array form is " +
      "a list of { field, operator, value } conditions ANDed together. " +
      // This union is opaque (`z.array(z.unknown())`), so there is no `operator` field to hang a
      // description on — it has to be named here or the vocabulary is undiscoverable.
      filterOperatorDescription,
  });

/**
 * Build a filter `where` schema locked to an explicit allow-list of filterable fields. SECURITY
 * CONTROL: `buildFilterWhereClause` (server/helpers/queryBuilder.ts) drops each condition's `field`
 * straight into a Mongo query key, so the open {@link filterWhereClause} lets a client filter on —
 * and value-probe — ANY field, `portalId` and `users.modulePermissions.*` included.
 *
 * Accepts all three Syncfusion/SlickGrid shapes: flat predicate, grouped predicate (recursed, every
 * leaf `field` checked), and `{ field: "a,b,c", operator: "globalsearch" }` — globalsearch is
 * NARROWED rather than rejected, disallowed fields dropped from the list.
 *
 * ⚠️ `operator` is CLOSED to {@link filterOperators}. It used to be a free string, justified by the
 * claim that "the UI emits `between`, `empty`, `null`, …" — those are the grid's INTERNAL option
 * keys, and both SlickGrid implementations translate them to backend tokens before the request:
 * `null`/`empty`/`notnull`/`notempty` become `isempty`/`isnotempty` (FilterModal
 * `getNullEmptyPredicate`, FilterComponent `buildEmptyNotEmptyPredicate`) and `between` is expanded
 * into a `greaterthanorequal` + `lessthanorequal` pair (`getDatePredicates`). Nothing on the wire
 * ever carried them, which is why they are absent from `filterOperators` and must NOT be added.
 *
 * The field allow-list remains the SECURITY control; the operator enum is a CORRECTNESS one, since
 * an unrecognised operator silently widened the result set instead of failing. `value` stays
 * scalars/arrays-of-scalars so a raw Mongo operator object (`{ $ne: null }`) cannot be smuggled in.
 */
/**
 * Allow-patterns for grids whose filterable columns are DYNAMIC per project and cannot be enumerated
 * statically. Pass via the `patterns` option of {@link filterWhereClauseFor}.
 *
 * - `customFieldFilterPattern` — `customFields.<id>` / `projectLevelCustomFields.<id>`, where <id> is
 *   a 24-hex ObjectId or a 36-char UUID v4 (SlickGrid1/customFields.tsx `getFieldKey`).
 * - `formQuestionFilterPattern` — bare client-generated `questionId` UUID v4, consumed by
 *   `buildFilterWhereClause(..., formsPatch=true)`.
 */
const CHOICE_LEAF = String.raw`(\.(id|name))?`;
export const customFieldFilterPattern = new RegExp(
  String.raw`^(customFields|projectLevelCustomFields)\.([a-fA-F0-9]{24}|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})${CHOICE_LEAF}$`,
);
export const formQuestionFilterPattern = new RegExp(
  String.raw`^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}${CHOICE_LEAF}$`,
);

export const splitFormQuestionField = (
  field: string,
): { questionId: string; leaf?: string } => {
  if (!/^.*\.(?:id|name)$/.test(field)) return { questionId: field };
  // The leaf (`id` / `name`) holds no dot, so the last dot is the one that separates it.
  const dot = field.lastIndexOf(".");
  return { questionId: field.slice(0, dot), leaf: field.slice(dot + 1) };
};

/**
 * Both dynamic-column patterns together. Safe to pass to ANY grid endpoint: these patterns
 * only match user-defined custom-field / form-question columns (`customFields.<id>`,
 * `<uuid>`) — never internal system fields — so they never reopen the arbitrary-field
 * hole, and including them prevents 400s on grids that render dynamic custom-field columns.
 */
export const dynamicCustomFieldPatterns: readonly RegExp[] = [
  customFieldFilterPattern,
  formQuestionFilterPattern,
];

/**
 * A single filter `value` exactly as {@link filterWhereClauseFor}'s internal `filterValue` constrains it
 * AT RUNTIME: scalars and arrays-of-scalars ONLY. That restriction is itself the security control — it is
 * what stops a client smuggling a raw Mongo operator object (`{ $ne: null }`, `{ $gt: "" }`) into a query
 * key, since the query builder drops values in verbatim. Do NOT widen the schema.
 */
type FilterConditionScalar = string | number | boolean | null;
export type FilterConditionValue =
  FilterConditionScalar | FilterConditionScalar[];

/**
 * One leaf/group condition of a `where` payload validated by {@link filterWhereClauseFor}, and the
 * element type its `z.infer` yields. Every member is optional because the schema accepts all three
 * Syncfusion/SlickGrid shapes, so nothing may be assumed present; the field ALLOW-LIST enforced at
 * runtime by `superRefine` — not this type — is the security control.
 *
 * ⚠️ `value` is deliberately `unknown`, a sound widening of {@link FilterConditionValue}: the web
 * grid's `PredicateShape` (web/src/services/queryBuilder.ts) declares `value: unknown`, and a
 * narrower declaration here stops `packages/web` compiling. Runtime rejection of `{ $ne: null }` is
 * unaffected. For the same reason do NOT add a `[key: string]: unknown` index signature — TypeScript
 * grants interfaces no implicit index signature, so it would reject every interface-typed caller.
 */
export interface FilterWhereCondition {
  // Each member is `| undefined` EXPLICITLY, not just `?`. Under `exactOptionalPropertyTypes` those
  // differ: `field?: string` means the key may be ABSENT but never explicitly `undefined`, whereas
  // Zod's `.optional()` produces exactly `string | undefined`. Without the annotation the inferred
  // schema output stops being assignable to this type.
  field?: string | undefined;
  operator?: string | undefined;
  value?: unknown;
  isComplex?: boolean | undefined;
  condition?: string | undefined;
  predicates?: FilterWhereCondition[] | undefined;
  /**
   * Syncfusion/SlickGrid case+accent flags. `looseObject` passes them through at runtime, and both are
   * already declared by {@link whereClauseSchema}'s predicate and by `queryBuilder.ts`'s FilterCondition.
   * They MUST be named here rather than left to an index signature: several `packages/web` actions wrap
   * their payload in type-fest's `Exact<>`, which turns any key absent from this interface into `never`.
   */
  ignoreCase?: boolean;
  ignoreAccent?: boolean;
}

/**
 * @param allowedFields exact field names a client may filter on.
 * @param opts.patterns optional RegExps; a field is also allowed if it matches any pattern
 *   (use for grids with dynamic custom-field / form-question columns — see the pattern consts).
 */
export function filterWhereClauseFor(
  allowedFields: readonly string[],
  opts: { patterns?: readonly RegExp[] } = {},
) {
  const allowed = new Set(allowedFields);
  const patterns = opts.patterns ?? [];
  const isAllowed = (field: string) =>
    allowed.has(field) || patterns.some((re) => re.test(field));
  const allowedList =
    allowedFields.join(", ") +
    (patterns.length ? " (plus dynamic custom-field columns)" : "");

  // Scalars and arrays-of-scalars only — never a raw object (blocks `{ $ne: ... }` etc.).
  const filterScalar = z.union([z.string(), z.number(), z.boolean(), z.null()]);
  const filterValue = z.union([filterScalar, z.array(filterScalar)]).optional();

  // Narrow a global-search field list to allow-listed columns only. Runs as a
  // `z.preprocess` (input coercion) rather than a trailing `.transform()` ON PURPOSE:
  // `.transform()` makes `z.toJSONSchema` throw ("Transforms cannot be represented"),
  // which would force every endpoint using this filter to fall back to an opaque
  // `payload` shape when advertised to API/MCP clients. `z.preprocess(fn, inner)` does
  // the same narrowing but stays JSON-Schema-representable. Do NOT switch this back to
  // `.transform()`.
  const narrowGlobalSearch = (raw: unknown) => {
    const cond = raw as { operator?: string; field?: unknown } | null;
    if (
      cond &&
      cond.operator === "globalsearch" &&
      typeof cond.field === "string"
    ) {
      const narrowed = cond.field
        .split(",")
        .map((f) => f.trim())
        .filter((f) => isAllowed(f));
      return { ...cond, field: narrowed.join(",") };
    }
    return raw;
  };

  // Recursive schemas need an explicit annotation (TypeScript cannot infer a self-referential type), and
  // that annotation is exactly what `z.infer` reports. Naming the real node type here — instead of the
  // bare `z.ZodType`, i.e. `ZodType<unknown, unknown>`, this used to carry — is what lets
  // `FilterWhereCondition` flow out to every routed grid endpoint. The INPUT side is left at `ZodType`'s
  // `unknown` default on purpose: `z.preprocess` genuinely accepts unknown input, so nothing that used to
  // parse stops parsing and no caller's request-payload type is narrowed.
  const condition: z.ZodType<FilterWhereCondition> = z.lazy(() =>
    z.preprocess(
      narrowGlobalSearch,
      z
        .looseObject({
          field: z.string().optional(),
          // CLOSED to `filterOperators`. This is the property `z.toJSONSchema` used to render as a
          // bare `{ "type": "string" }`, leaving MCP callers to guess `eq` and silently lose their
          // filter; as an enum it is both advertised and enforced. `.optional()` is kept: a leaf
          // with no operator is legal (the builder defaults it to "equal") and group nodes
          // (`isComplex` + `predicates`) carry none.
          operator: z
            .enum(filterOperators)
            .optional()
            .meta({ description: filterOperatorDescription }),
          value: filterValue,
          isComplex: z.boolean().optional(),
          condition: z.string().optional(),
          predicates: z.array(condition).optional(),
        })
        .superRefine((cond, ctx) => {
          // Grouped conditions carry no `field`; their leaves are validated recursively
          // via `predicates`. Global search is narrowed (not rejected) by the preprocess
          // above, so it does not need an allow-list check here.
          if (cond.operator === "globalsearch") return;
          if (typeof cond.field !== "string" || cond.field.length === 0) return;
          if (!isAllowed(cond.field)) {
            ctx.addIssue({
              code: "custom",
              path: ["field"],
              message: `Filtering on "${cond.field}" is not allowed. Filterable fields: ${allowedList}.`,
            });
          }
        }),
    ),
  );

  return z
    .array(condition)
    .optional()
    .meta({
      description:
        "Filter for list/grid queries — an ARRAY of conditions ANDed together, e.g. " +
        `[{ field: "${allowedFields[0] ?? "name"}", operator: "contains", value: "Tower-A" }]. ` +
        `Only these fields may be filtered: ${allowedList}.`,
    });
}

/**
 * Sort configuration for grid queries.
 * Specifies the field to sort by and direction.
 */
export const sortConfig = z
  .object({
    // Non-empty: an empty sort field resolves to `{ "": -1 }`, which Mongo rejects with
    // "FieldPath cannot be constructed with empty string" — reject it at the boundary instead.
    name: z.string().min(1).meta({
      description: "Field name to sort by",
    }),
    direction: z.enum(["ascending", "descending"]).optional().meta({
      description: "Sort direction, defaults to ascending if not specified",
    }),
  })
  .meta({
    description: "Sort configuration for grid queries",
  });

/**
 * Array of sort configurations for multi-column sorting.
 */
export const sortConfigArray = z.array(sortConfig).optional().meta({
  description: "Array of sort configurations for multi-column sorting",
});

/**
 * Build a `sorted` schema locked to an allow-list of sortable columns; companion to
 * {@link filterWhereClauseFor}. `buildFilterSortClause` drops `sortClause[0].name` straight into a
 * Mongo sort key, so the open {@link sortConfig} lets a client order-probe ANY field and force an
 * unindexed blocking `$sort` (DoS). Disallowed columns are REJECTED at the Zod boundary (400).
 *
 * Drop-in for `sortConfigArray`. `opts.patterns` (pass {@link dynamicCustomFieldPatterns}) also
 * allows dynamic custom-field / form-question columns. The query builder applies only
 * `sortClause[0]`, but every entry is still checked, so a bad column at index 1+ still 400s.
 */
export function sortConfigFor(
  allowedFields: readonly string[],
  opts: { patterns?: readonly RegExp[] } = {},
) {
  const allowed = new Set(allowedFields);
  const patterns = opts.patterns ?? [];
  const isAllowed = (name: string) =>
    allowed.has(name) || patterns.some((re) => re.test(name));
  const allowedList =
    allowedFields.join(", ") +
    (patterns.length ? " (plus dynamic custom-field columns)" : "");

  const entry = z
    .object({
      // Non-empty (an empty sort field resolves to `{ "": -1 }`, which Mongo rejects) AND allow-listed.
      name: z
        .string()
        .min(1)
        .meta({
          description: `Column to sort by. Sortable columns: ${allowedList}.`,
        }),
      direction: z.enum(["ascending", "descending"]).optional().meta({
        description: "Sort direction, defaults to ascending if not specified",
      }),
    })
    // superRefine (NOT .transform) keeps the schema z.toJSONSchema-representable, like filterWhereClauseFor.
    .superRefine((cfg, ctx) => {
      if (!isAllowed(cfg.name)) {
        ctx.addIssue({
          code: "custom",
          path: ["name"],
          message: `Sorting on "${cfg.name}" is not allowed. Sortable columns: ${allowedList}.`,
        });
      }
    });

  return z
    .array(entry)
    .optional()
    .meta({
      description:
        "Sort configuration for list/grid queries — an array of { name, direction? }; only the first entry is " +
        `applied server-side. Only these columns may be sorted: ${allowedList}.`,
    });
}

// ============================================================================
// Common Response Schemas
// ============================================================================

/**
 * Success branch: status is true.
 */
export const statusResponseSuccess = z.object({
  status: z.literal(true),
});

/**
 * Failure branch: status is false, and the reason travels as `errorCode` (plus `params` for a
 * templated sentence). The server never puts user-facing English on the wire.
 */
export const statusResponseFailure = z.object({
  status: z.literal(false),
  /**
   * Names the refusal. The CLIENT turns it into a sentence through `helpers/errorCodeMapper` — the
   * server never puts user-facing English on the wire, which is what makes every refusal
   * translatable without the backend knowing anything about locales.
   *
   * REQUIRED: every refusal names its own code at the branch that decides it, so a failure body
   * without one would reach the client with nothing to turn into a sentence.
   */
  errorCode: z.number(),
  /**
   * Runtime values for an `errorCode` whose message is a `{{placeholder}}` template — the mechanism
   * that lets a refusal name a specific plan code, field name or count WITHOUT the backend authoring
   * the sentence. The frontend passes these straight to `t(template, params)`, which interpolates
   * and translates in one step (`packages/web/src/i18n/errorContent.ts`).
   *
   * `count` is special: an entry may declare `errorMessagePlural`, and `getErrorMessageTemplate`
   * picks the plural sentence when `count > 1`.
   *
   * Only send it for codes whose mapper entry declares `dynamicParams`; anything else is ignored.
   */
  params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});

/**
 * Status-only response, for routes that return no payload.
 * When status is false, `errorCode` names the reason; the client resolves the sentence from it.
 * Use {@link standardResponse} when there IS a payload.
 */
export const statusResponse = z.discriminatedUnion("status", [
  statusResponseSuccess,
  statusResponseFailure,
]);

/**
 * THE standard response shape; every route uses this or {@link statusResponse}.
 *   success -> { status: true, ...payload }   failure -> { status: false, errorCode?, params? }
 *
 * The payload sits on the SUCCESS ARM ONLY, so callers must narrow on `status` first.
 *
 * ⚠️ A route needing structured data on FAILURE (`lockedUntil`, `attemptsRemaining`) builds its own
 * union locally from `statusResponseSuccess` / `statusResponseFailure` — never widen this helper.
 */
export function standardResponse<T extends z.ZodRawShape>(shape: T) {
  return z.discriminatedUnion("status", [
    statusResponseSuccess.extend(shape),
    statusResponseFailure,
  ]);
}

/**
 * The `responseSchema` of a route that answers with raw bytes (a file, an image, a model, an archive) instead of
 * JSON. There is no JSON body to type: the typed client types such a call by the `responseType` it asks for
 * (`ArrayBuffer` / `Blob`), and a URL handed to the browser is never parsed. Each route notes beside its contract
 * what its refusals send; a schema typing only those would misdescribe every successful response.
 */
export const byteStreamResponse = z.unknown().meta({
  description: "Raw bytes (a file, an image, a model or an archive), not JSON.",
});

/**
 * ⚠️ LEGACY, and NOT a route envelope to copy: `apiResponse`, {@link apiResponseWithData} and
 * {@link baseResponse} below are flat `status: z.boolean()` shapes, which discriminate nothing — a
 * payload declared next to them is in range on the refusal body too. Swept 2026-09-12: NOTHING in
 * packages/server, packages/web or packages/mobile imports any of the three, so there is no route to
 * convert and they are left exactly as they are. Use {@link statusResponse} / {@link standardResponse}
 * for anything new; do not give these three a first importer.
 */
export const apiResponse = z
  .object({
    status: z.boolean().meta({
      description: "Indicates whether the operation was successful",
    }),
    error: z.string().optional().meta({
      description: "Short error description",
    }),
    errorCode: z.number().optional().meta({
      description: "Numeric error code for programmatic handling",
    }),
    message: z.string().optional().meta({
      description: "Success or informational message",
    }),
  })
  .meta({
    description: "Standard API response with optional error fields",
  });

/**
 * Standard API response with data field.
 * Use this for responses that return data on success.
 */
export const apiResponseWithData = apiResponse
  .extend({
    data: z.unknown().optional().meta({
      description: "Response data payload",
    }),
  })
  .meta({
    description: "Standard API response with data field",
  });

/**
 * Minimal service response shape shared by many backend service methods.
 * Mirrors the former `BaseResponse` interface (helpers/scheduleInterface.ts).
 */
export const baseResponse = z
  .object({
    status: z.boolean(),
    message: z.string().optional(),
    errorCode: z.union([z.number(), z.string()]).optional(),
  })
  .meta({
    description: "Base service response with optional message/error fields",
  });
export type BaseResponse = z.infer<typeof baseResponse>;

// ============================================================================
// Grid Request Value Schema
// ============================================================================

/**
 * Common grid request value schema.
 * Used for paginated, sortable, filterable grid requests.
 */
export const gridRequestValue = z
  .object({
    where: filterWhereClause,
    sorted: sortConfigArray,
    skip: z.number().nonnegative().optional().meta({
      description: "Number of records to skip for pagination",
    }),
    take: z.number().nonnegative().optional().meta({
      description: "Number of records to take for pagination",
    }),
    requiresCounts: z.boolean().optional().meta({
      description: "Whether to return total count for pagination",
    }),
  })
  .meta({
    description:
      "Common grid request parameters for pagination, sorting, and filtering",
  });

// ============================================================================
// Where Clause / Predicate Schema
// ============================================================================

/**
 * Single filter predicate for grid queries.
 */
const predicateSchema = z.object({
  field: z.string(),
  // Same closed enum as `filterWhereClauseFor`'s `operator`: these predicates feed the SAME
  // `buildFilterWhereClause`, so leaving this one open would keep the silent-drop gap on every
  // generic-grid endpoint that uses `whereClauseSchema`. Required here, so there is no
  // undefined-means-"equal" case to allow.
  operator: z
    .enum(filterOperators)
    .meta({ description: filterOperatorDescription }),
  value: z.unknown(),
  isComplex: z.boolean().optional(),
  ignoreCase: z.boolean().optional(),
  ignoreAccent: z.boolean().optional(),
});

/**
 * Where clause schema supporting both simple predicates and compound conditions.
 * Used across multiple domains (estimates, currency, etc.) for grid filtering.
 */
export const whereClauseSchema = z
  .array(
    z.union([
      predicateSchema,
      z.object({
        condition: z.string(),
        isComplex: z.boolean().optional(),
        ignoreCase: z.boolean().optional(),
        ignoreAccent: z.boolean().optional(),
        predicates: z.array(predicateSchema),
      }),
    ]),
  )
  .optional();

// ============================================================================
// Type Exports
// ============================================================================

export type ObjectIdString = z.infer<typeof objectIdString>;
export type EmailString = z.infer<typeof emailString>;
export type TempUploadKey = z.infer<typeof tempUploadKey>;
export type FilterWhereClause = z.infer<typeof filterWhereClause>;
export type SortConfig = z.infer<typeof sortConfig>;
export type SortConfigArray = z.infer<typeof sortConfigArray>;
export type StatusResponse = z.infer<typeof statusResponse>;
export type ApiResponse = z.infer<typeof apiResponse>;
export type ApiResponseWithData = z.infer<typeof apiResponseWithData>;
export type GridRequestValue = z.infer<typeof gridRequestValue>;
export type WhereClause = z.infer<typeof whereClauseSchema>;
